import { ContactInfo, contactMatches } from "@common-models/contact-info";
import { isExtensionContext } from "@common-services/detect-context";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { contactSelectByQuery } from "@common-reducers/DBServiceThunks";
import { CommonRootState } from "@common/types/common-root-state-type";
import { sortContactsByGroupThenName } from "@common/utils/contact-sort-utils";
import { Chat, WhatsappContact } from "@bt-common/interfaces/whatsapp-types";

export type ThemeType = "light" | "dark";


export interface UserInfo {
  email: string;
  id: string;
}
export interface WhatsAppState {
  currentChat: Chat | null;
  chatList: Chat[];
  chatMap: { [key: string]: Chat };
  theme: ThemeType;
  errorState: boolean;
  completeDetailsClosed: boolean;
  userInfo?: UserInfo;
  invalidWANumber?: string;
  me?: WhatsappContact;
  wapiInitialized: boolean;
  assetMap: Record<string, File>,
  currentExtensionVersion: string,
  importedContactsArray: ContactInfo[];
  showWizard?: boolean;
  hoveredDate?: any;
  isReply?: any;
  mentions?: { id: string, display: string }[];
  redisContactsUpdateTimestamp?: string;
  addMsgInCalendarContact?: any;
  extensionSidebarSearchToken?: string;
  highlightedContactId?: string;
  appState?: string;
}

const initialState: WhatsAppState = {
  theme: "light",
  currentChat: null,
  chatMap: {},
  chatList: [],
  errorState: false,
  completeDetailsClosed: false,
  wapiInitialized: false,
  assetMap: {},
  currentExtensionVersion: "",
  importedContactsArray: [],
  showWizard: false,
  hoveredDate: undefined,
  isReply: null,
  mentions: undefined,
  redisContactsUpdateTimestamp: undefined,
  addMsgInCalendarContact: null,
  extensionSidebarSearchToken: undefined,
  highlightedContactId: undefined,
  appState: undefined,
};

const WhatsAppSlice = createSlice({
  name: "whatsApp",
  initialState,
  reducers: {
    setAsset: (state: WhatsAppState, action: PayloadAction<{ url: string, file: File }>) => {
      state.assetMap[action.payload.url] = action.payload.file;
    },

    privateSetCurrentChat(state, action: PayloadAction<Chat | null>) {
      console.log("privateSetCurrentChat");
      state.currentChat = action.payload;
    },

    setWAchats(state, action: PayloadAction<Chat[]>) {
      console.log("setWAchats");
      state.chatMap = action.payload.reduce((chatMap, chat) => {
        const chatId = chat?.id?._serialized;
        if (chatId) {
          chatMap[chatId] = chat;
        }
        return chatMap;
      }, {} as Record<string, Chat>);
      state.chatList = Object.keys(state.chatMap).map(
        (chatId) => state.chatMap[chatId]
      ) ?? [];
    },
    setTheme(state, action: PayloadAction<ThemeType>) {
      state.theme = action.payload;
    },
    setErrorState(state, action: PayloadAction<boolean>) {
      state.errorState = action.payload;
    },
    setUserInfo(state, action: PayloadAction<UserInfo>) {
      state.userInfo = action.payload;
    },
    setInvalidWANumber: (state: WhatsAppState, action: PayloadAction<string>) => {
      state.invalidWANumber = action.payload;
    },
    setMe: (state: WhatsAppState, action: PayloadAction<WhatsappContact>) => {
      state.me = action.payload;
    },
    setWapiInitialized: (state: WhatsAppState, action: PayloadAction<boolean>) => {
      state.wapiInitialized = action.payload;
    },
    setCurrentExtensionVersion(state, action: PayloadAction<string>) {
      state.currentExtensionVersion = action.payload
    },
    setImportedContactsArray(state, action: PayloadAction<any[]>) {
      state.importedContactsArray = action.payload
    },
    setShowWizard(state, action: PayloadAction<boolean>) {
      state.showWizard = action.payload
    },
    setHoveredDate: (state: WhatsAppState, action: PayloadAction<any>) => {
      state.hoveredDate = action.payload;
    },
    setAddMsgInCalendarContact: (state: WhatsAppState, action: PayloadAction<any>) => {
      state.addMsgInCalendarContact = action.payload;
    },
    setIsReply: (state: WhatsAppState, action: PayloadAction<any>) => {
      state.isReply = action.payload;
    },
    setMentions: (state: WhatsAppState, action: PayloadAction<any>) => {
      state.mentions = action.payload;
    },
    setExtensionSidebarSearchToken: (state: WhatsAppState, action: PayloadAction<any>) => {
      state.extensionSidebarSearchToken = action.payload;
    },
    setHighlightedContactId: (state: WhatsAppState, action: PayloadAction<string | undefined>) => {
      state.highlightedContactId = action.payload;
    },
    setAppState: (state: WhatsAppState, action: PayloadAction<string>) => {
      state.appState = action.payload;
    },
  },
});

// Stable filter object so the memoized contactSelectByQuery cache is not busted by a fresh literal each call
const notDeletedContactFilter = {
  $or: [
    { deleted: { $exists: false } },
    { deleted: false }
  ],
};

export const selectMergedContactMap = createSelector(
  [
    (state: CommonRootState) => state?.DBWaContactsCacheReducer?.ids,
    (state: CommonRootState) => state?.DBWaContactsCacheReducer?.entities,
    (state: CommonRootState) => state.DBContactReducer,
  ],
  (waContactIds, waContactEntities, dbContactState) => {
    const contactList = (waContactIds ?? []).map(id => waContactEntities?.[id]);
    const importedContactList: ContactInfo[] = contactSelectByQuery(dbContactState, notDeletedContactFilter);

    let _mergedContactMap: Record<string, ContactInfo> = {};
    for (const waContact of contactList) {
      // Include all contacts including @lid (used by modern WhatsApp chats)
      if (waContact?.id?._serialized) {
        _mergedContactMap[waContact.id._serialized] = {
          ...waContact,
          whatsappId: waContact.id._serialized
        };
      }
    }

    for (const contact of importedContactList) {
      if (contact?.whatsappId) {
        _mergedContactMap[contact.whatsappId] = {
          ...(_mergedContactMap[contact.whatsappId] ?? {}),
          ...contact
        };
      }
    }

    return _mergedContactMap;
  }
);

export const selectMergedContactMapWithNamesOnly = createSelector([
  selectMergedContactMap
], (mergedContactMap: Record<string, ContactInfo>) => {
  // create from mergedContactMap a new map of all the contacts that has the 'name' field not undefined
  // and the value is the contact itself
  const mergedContactMapWithNamesOnly = Object
    .keys(mergedContactMap)
    .filter(whatsappId => mergedContactMap[whatsappId].name && mergedContactMap[whatsappId].name !== '')
    .reduce((mergedContactMapWithNamesOnly, whatsappId) => {
      mergedContactMapWithNamesOnly[whatsappId] = mergedContactMap[whatsappId];
      return mergedContactMapWithNamesOnly;
    }, {} as Record<string, ContactInfo>);


  return mergedContactMapWithNamesOnly
});

export const selectMergedContactList = createSelector([
  (state: CommonRootState) => selectMergedContactMap(state)
], (mergedContactMap: Record<string, ContactInfo>) => {
  return Object
    .keys(mergedContactMap)
    .map(whatsappId => mergedContactMap[whatsappId]);
});


export const getContactListForMentions = createSelector([
  (state: CommonRootState) => selectMergedContactList(state),
  (state: CommonRootState) => state
], (mergedContactList, state) => {
  const mentions = state.WhatsAppReducer.mentions;
  if (mentions) {
    return mentions
  }

  // Fallback to merged contact list when mentions not set
  const contactListForMentions = mergedContactList
    .filter(contact => contact.whatsappId && (contact.whatsappId.includes('c.us') || contact.whatsappId.includes('g.us')))
    .map(contact => {
      const whatsappId = contact.whatsappId ?? '';
      return ({
        id: whatsappId,
        display: contact.name ?? whatsappId.split('@')[0]
      })
    })

  return contactListForMentions
});

export const selectFilteredMergedContactList = createSelector([
  (state: CommonRootState) => selectMergedContactMap(state),
  (state: CommonRootState, filterValue: string) => filterValue,
], (mergedContactMap: Record<string, ContactInfo>, filterValue: string) => {
  return Object
    .keys(mergedContactMap)
    .map(whatsappId => mergedContactMap[whatsappId])
    .filter(contact => contactMatches(filterValue, contact));
});


export const getContactsForRedisUpdate = createSelector([
  (state: CommonRootState) => selectMergedContactMap(state),
], (mergedContactMap: Record<string, ContactInfo>) => {
  if (isExtensionContext()) {
    return Object
      .keys(mergedContactMap)
      .map(whatsappId => mergedContactMap[whatsappId])
  }
});


export const selectedSelectedContactCount = createSelector([
  (state: CommonRootState) => state.WhatsAppReducer.importedContactsArray,
], (importedContactsArray) => {
  return importedContactsArray.length;
});

export const selectFilteredContactList = createSelector([
  (state: CommonRootState) => state.WhatsAppReducer.importedContactsArray,
  (state: CommonRootState) => selectMergedContactList(state),
  (state: CommonRootState) => state.ContactTableReducer.filterValue,
  (state: CommonRootState) => state.ContactTableReducer.showSelected,
  (state: CommonRootState) => state.CampaignReducer.contactListPanelState.selectedListId,
], (importedContactsArray: ContactInfo[], mergedContactList: ContactInfo[], filterValue: string, showSelected: boolean, selectedListId) => {
  const selectedIdList = importedContactsArray.map(contact => contact.whatsappId);
  const filteredContactList = mergedContactList
    .filter((contact: ContactInfo) => {
      const matchesFilter = filterValue ? contactMatches(filterValue, contact) : true;
      const matchesSelected = showSelected ? selectedIdList.indexOf(contact.id?._serialized ?? contact.whatsappId ?? '') > -1 : true;

      return matchesFilter && matchesSelected;
    });

  return sortContactsByGroupThenName(filteredContactList);
});






export const {
  privateSetCurrentChat,
  setWAchats,
  setTheme,
  setErrorState,
  setUserInfo,
  setInvalidWANumber,
  setMe,
  setWapiInitialized,
  setCurrentExtensionVersion,
  setImportedContactsArray,
  setShowWizard,
  setHoveredDate,
  setAddMsgInCalendarContact,
  setIsReply,
  setMentions,
  setExtensionSidebarSearchToken,
  setHighlightedContactId,
  setAppState,
} = WhatsAppSlice.actions;
export const WhatsappActions = WhatsAppSlice.actions;

export default WhatsAppSlice.reducer;
